'use client';

import {
  Wallet, Clock, TrendingUp, TrendingDown, AlertTriangle, Users, FileText,
  Shield, Banknote, BarChart3, Zap, Receipt,
  Sparkles,
  PieChart,
  CalendarClock,
  ShieldCheck,
  Percent,
  GitCompare,
  LineChart,
  CalendarCheck,
  Copy,
  Bell,
  Activity,
  Calendar,
  Coins,
  PiggyBank,
  Package,
} from 'lucide-react';
import type { WidgetDefinition } from './types';
import {
  RevenueMonthWidget,
  OutstandingWidget,
  DsoWidget,
  CashPositionWidget,
  AgingWidget,
  TopDebitorsWidget,
  OverdueInvoicesWidget,
  RevenueChartWidget,
  TopClientsWidget,
  RecentInvoicesWidget,
  AnafStatusWidget,
  QuickActionsWidget,
  CashFlowForecastWidget,
  WhatIfWidget,
  VatDonutWidget,
  DeclaratiiWidget,
  SpvStatusWidget,
  CollectionRateWidget,
  YoyComparisonWidget,
  OverdueTrendWidget,
  UpcomingRecurringWidget,
  DetectedRecurringWidget,
  AlertsCenterWidget,
  ActivityFeedWidget,
  InvoicedTodayWidget,
  CollectedTodayWidget,
  CollectedMonthWidget,
  OverdueKpiWidget,
  PendingStockDecrementWidget,
} from './implementations';

export const WIDGET_ORDER = [
  'cash-position',
  'revenue-month',
  'invoiced-today',
  'collected-today',
  'collected-month',
  'dso',
  'outstanding',
  'overdue-kpi',
  'pending-stock-decrement',
  'aging',
  'top-debitors',
  'overdue-invoices',
  'cashflow-forecast',
  'whatif',
  'revenue-chart',
  'top-clients',
  'recent-invoices',
  'anaf-status',
  'vat-donut',
  'declaratii',
  'spv-status',
  'collection-rate',
  'yoy-comparison',
  'overdue-trend',
  'alerts',
  'upcoming-recurring',
  'detected-recurring',
  'activity-feed',
  'quick-actions',
];

export const WIDGET_REGISTRY: Record<string, WidgetDefinition> = {
  'cash-position': {
    id: 'cash-position',
    title: 'Cash Position',
    description: 'Total încasări lună (cheltuielile vor fi adăugate ulterior)',
    category: 'kpi',
    size: 'M',
    isNew: true,
    icon: Wallet,
    Component: CashPositionWidget,
  },
  'revenue-month': {
    id: 'revenue-month',
    title: 'Facturat · lună',
    description: 'Total facturat în luna curentă',
    category: 'kpi',
    size: 'S',
    icon: TrendingUp,
    Component: RevenueMonthWidget,
  },
  'invoiced-today': {
    id: 'invoiced-today',
    title: 'Facturat azi',
    description: 'Sumă facturată azi (exclude draft și anulate)',
    category: 'kpi',
    size: 'S',
    isNew: true,
    icon: Calendar,
    Component: InvoicedTodayWidget,
  },
  'collected-today': {
    id: 'collected-today',
    title: 'Încasat azi',
    description: 'Plăți înregistrate în ziua curentă',
    category: 'kpi',
    size: 'S',
    isNew: true,
    icon: Coins,
    Component: CollectedTodayWidget,
  },
  'collected-month': {
    id: 'collected-month',
    title: 'Încasat · lună',
    description: 'Total încasat în luna curentă',
    category: 'kpi',
    size: 'S',
    isNew: true,
    icon: PiggyBank,
    Component: CollectedMonthWidget,
  },
  'dso': {
    id: 'dso',
    title: 'DSO',
    description: 'Zile medii între emitere și încasare',
    category: 'kpi',
    size: 'S',
    isNew: true,
    icon: Clock,
    Component: DsoWidget,
  },
  'outstanding': {
    id: 'outstanding',
    title: 'Facturi neîncasate',
    description: 'Toate facturile neplătite (în termen + depășite)',
    category: 'kpi',
    size: 'S',
    icon: Wallet,
    Component: OutstandingWidget,
  },
  'overdue-kpi': {
    id: 'overdue-kpi',
    title: 'Facturi cu scadența depășită',
    description: 'Total facturi cu scadența depășită și neîncasate',
    category: 'overdue',
    size: 'S',
    isNew: true,
    icon: AlertTriangle,
    Component: OverdueKpiWidget,
  },
  'pending-stock-decrement': {
    id: 'pending-stock-decrement',
    title: 'Stoc nedescărcat',
    description: 'Facturi emise pentru care stocul încă nu a fost descărcat',
    category: 'ops',
    size: 'S',
    isNew: true,
    icon: Package,
    Component: PendingStockDecrementWidget,
  },
  'aging': {
    id: 'aging',
    title: 'Restante pe vechime',
    description: 'Buckets 0-30 / 31-60 / 61-90 / 90+ zile',
    category: 'overdue',
    size: 'M',
    isNew: true,
    icon: TrendingDown,
    Component: AgingWidget,
  },
  'top-debitors': {
    id: 'top-debitors',
    title: 'Top debitori',
    description: 'Clienți cu cele mai mari restanțe',
    category: 'overdue',
    size: 'L',
    isNew: true,
    icon: AlertTriangle,
    Component: TopDebitorsWidget,
  },
  'overdue-invoices': {
    id: 'overdue-invoices',
    title: 'Facturi cu scadența depășită',
    description: 'Top 5 facturi cu scadența depășită — listă',
    category: 'overdue',
    size: 'L',
    icon: Clock,
    Component: OverdueInvoicesWidget,
  },
  'cashflow-forecast': {
    id: 'cashflow-forecast',
    title: 'Prognoză cash flow · 90 zile',
    description: 'Încasări așteptate vs ieșiri pe 90 zile',
    category: 'cashflow',
    size: 'XL',
    isNew: true,
    icon: TrendingUp,
    Component: CashFlowForecastWidget,
  },
  'whatif': {
    id: 'whatif',
    title: 'Scenarii What-if',
    description: 'Simulează impact recuperare restanțe + termen plată',
    category: 'cashflow',
    size: 'L',
    isNew: true,
    icon: Sparkles,
    Component: WhatIfWidget,
  },
  'revenue-chart': {
    id: 'revenue-chart',
    title: 'Evoluție facturat',
    description: 'Total facturat lunar — ultimele 12 luni',
    category: 'sales',
    size: 'XL',
    icon: BarChart3,
    Component: RevenueChartWidget,
  },
  'top-clients': {
    id: 'top-clients',
    title: 'Top clienți',
    description: 'Valoare totală pe client',
    category: 'sales',
    size: 'L',
    isNew: true,
    icon: Users,
    Component: TopClientsWidget,
  },
  'recent-invoices': {
    id: 'recent-invoices',
    title: 'Documente recente',
    description: 'Ultimele 5 documente emise (factură, proformă, aviz, storno)',
    category: 'sales',
    size: 'L',
    icon: FileText,
    Component: RecentInvoicesWidget,
  },
  'anaf-status': {
    id: 'anaf-status',
    title: 'Status ANAF',
    description: 'Urgent / pending / validate',
    category: 'anaf',
    size: 'M',
    icon: Shield,
    Component: AnafStatusWidget,
  },
  'vat-donut': {
    id: 'vat-donut',
    title: 'TVA · lună',
    description: 'Colectat vs deductibil vs de plată',
    category: 'anaf',
    size: 'M',
    isNew: true,
    icon: PieChart,
    Component: VatDonutWidget,
  },
  'declaratii': {
    id: 'declaratii',
    title: 'Declarații scadente',
    description: 'D100, D112, D300, D394 cu countdown',
    category: 'anaf',
    size: 'M',
    isNew: true,
    icon: CalendarClock,
    Component: DeclaratiiWidget,
  },
  'spv-status': {
    id: 'spv-status',
    title: 'Status SPV',
    description: 'Conexiune ANAF + token expiry',
    category: 'anaf',
    size: 'M',
    isNew: true,
    icon: ShieldCheck,
    Component: SpvStatusWidget,
  },
  'collection-rate': {
    id: 'collection-rate',
    title: 'Rată încasare',
    description: '% încasat din emis + trend 6 luni',
    category: 'kpi',
    size: 'M',
    isNew: true,
    icon: Percent,
    Component: CollectionRateWidget,
  },
  'yoy-comparison': {
    id: 'yoy-comparison',
    title: 'Comparație perioadă',
    description: 'Luna curentă vs media ultimelor 11 luni',
    category: 'sales',
    size: 'M',
    isNew: true,
    icon: GitCompare,
    Component: YoyComparisonWidget,
  },
  'overdue-trend': {
    id: 'overdue-trend',
    title: 'Tendință restanțe',
    description: 'Evoluție restanțe ultimele 6 luni',
    category: 'overdue',
    size: 'L',
    isNew: true,
    icon: LineChart,
    Component: OverdueTrendWidget,
  },
  'alerts': {
    id: 'alerts',
    title: 'Alerte active',
    description: 'Sumar: termen ANAF, respinse, restante critice',
    category: 'ai',
    size: 'L',
    isNew: true,
    icon: Bell,
    Component: AlertsCenterWidget,
  },
  'upcoming-recurring': {
    id: 'upcoming-recurring',
    title: 'Recurențe următoare',
    description: 'Facturi programate automat în 14 zile',
    category: 'ops',
    size: 'L',
    isNew: true,
    icon: CalendarCheck,
    Component: UpcomingRecurringWidget,
  },
  'detected-recurring': {
    id: 'detected-recurring',
    title: 'Recurențe detectate AI',
    description: 'Clienți cu pattern repetitiv — candidați recurență',
    category: 'ai',
    size: 'L',
    isNew: true,
    icon: Copy,
    Component: DetectedRecurringWidget,
  },
  'activity-feed': {
    id: 'activity-feed',
    title: 'Activitate recentă',
    description: 'Timeline: facturi, proforme, avize, chitanțe, plăți, NIR',
    category: 'ops',
    size: 'Full',
    isNew: true,
    icon: Activity,
    Component: ActivityFeedWidget,
  },
  'quick-actions': {
    id: 'quick-actions',
    title: 'Acțiuni rapide',
    description: 'Shortcuts către acțiuni frecvente',
    category: 'ops',
    size: 'L',
    icon: Zap,
    Component: QuickActionsWidget,
  },
};
